import React, { useState, useEffect } from 'react';
import { Dumbbell, Menu, X } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const translations = {
  de: {
    home: 'Startseite',
    features: 'Vorteile',
    games: 'Programme',
    faq: 'FAQ',
    contact: 'Kontakt',
  },
  en: {
    home: 'Home',
    features: 'Features',
    games: 'Games',
    faq: 'FAQ',
    contact: 'Contact',
  }
};

function Header({ language, onLanguageChange }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-darker/95 backdrop-blur-lg shadow-lg shadow-primary/10' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => scrollToSection('hero')}>
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-primary via-secondary to-accent rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                <Dumbbell className="w-7 h-7 text-white" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-white tracking-wider">
                Fit<span className="text-accent">Xcel</span>
              </span>
              <span className="block text-xs text-muted font-body tracking-widest">24kitchenai.com</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { key: 'home', id: 'hero' },
              { key: 'features', id: 'features' },
              { key: 'games', id: 'games' },
              { key: 'faq', id: 'faq' },
              { key: 'contact', id: 'footer' },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.id)}
                className="text-slate-300 hover:text-white font-body text-lg font-medium tracking-wide relative group transition-colors"
              >
                {t[item.key]}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-accent group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </nav>

          {/* Language Switcher */}
          <div className="flex items-center gap-4">
            <div className="flex bg-surface/50 rounded-full p-1 border border-slate-700">
              <button
                onClick={() => onLanguageChange('de')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-semibold transition-all duration-300 ${
                  language === 'de'
                    ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ReactCountryFlag
                  countryCode="DE"
                  svg
                  style={{
                    width: '1.2em',
                    height: '1.2em',
                    borderRadius: '2px',
                  }}
                  title="Deutsch"
                />
                DE
              </button>
              <button
                onClick={() => onLanguageChange('en')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-semibold transition-all duration-300 ${
                  language === 'en'
                    ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ReactCountryFlag
                  countryCode="GB"
                  svg
                  style={{
                    width: '1.2em',
                    height: '1.2em',
                    borderRadius: '2px',
                  }}
                  title="English"
                />
                EN
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${
        mobileMenuOpen ? 'max-h-96' : 'max-h-0'
      }`}>
        <nav className="bg-surface/95 backdrop-blur-lg border-t border-slate-700 px-4 py-4 space-y-2">
          {[
            { key: 'home', id: 'hero' },
            { key: 'features', id: 'features' },
            { key: 'games', id: 'games' },
            { key: 'faq', id: 'faq' },
            { key: 'contact', id: 'footer' },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left text-slate-300 hover:text-white font-body text-lg py-3 px-4 rounded-lg hover:bg-primary/20 transition-all"
            >
              {t[item.key]}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default Header;
